import { useEffect, useMemo, useState } from "react";
import { Alert } from "react-bootstrap";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  organizerGiftReferralLinkApi,
  organizerPhysicalListEventsApi,
} from "../../api/api";
import { handleError } from "../../helpers/handleError";
import SpinLoading from "../../components/spinLoading/SpinLoading";
import OrganizerPillSelect from "../components/OrganizerPillSelect";
import "../dashboard.css";

const copyTextToClipboard = async (value) => {
  const text = String(value || "");
  if (!text) throw new Error("EMPTY_TEXT");
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const input = document.createElement("textarea");
  input.value = text;
  input.setAttribute("readonly", "readonly");
  input.style.position = "absolute";
  input.style.left = "-9999px";
  document.body.appendChild(input);
  input.select();
  document.execCommand("copy");
  document.body.removeChild(input);
};

const OrganizerReferrals = () => {
  const auth = useSelector((state) => state.auth);
  const sellerId = String(auth?.user?._id || auth?.user?.id || auth?.user?.userid || "");

  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [referralInfo, setReferralInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [referralLoading, setReferralLoading] = useState(false);
  const [error, setError] = useState("");
  const [copyFeedbackTick, setCopyFeedbackTick] = useState(0);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        setLoading(true);
        const res = await organizerPhysicalListEventsApi();
        const list = Array.isArray(res?.data?.info) ? res.data.info : [];
        setEvents(list);
      } catch (err) {
        handleError(err);
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
  }, []);

  const eventOptions = useMemo(
    () =>
      events
        .map((ev) => {
          const id = ev?.id ?? ev?._id ?? ev?.eventid;
          if (!id) return null;
          return { value: String(id), label: ev?.name || "Evento sin nombre" };
        })
        .filter(Boolean),
    [events]
  );

  useEffect(() => {
    if (eventId) {
      setReferralInfo(null);
      setCopyFeedbackTick(0);
      return;
    }
    if (eventOptions.length === 1) {
      setEventId(String(eventOptions[0].value || ""));
    }
  }, [eventId, eventOptions]);

  useEffect(() => {
    if (!copyFeedbackTick) return undefined;

    const timeoutId = window.setTimeout(() => {
      setCopyFeedbackTick(0);
    }, 2200);

    return () => window.clearTimeout(timeoutId);
  }, [copyFeedbackTick]);

  const selectedEventLabel =
    eventOptions.find((item) => item.value === eventId)?.label || "Evento seleccionado";

  const handleGenerateReferralLink = async () => {
    if (!sellerId) return;
    if (!eventId) {
      setError("Selecciona un evento para generar el link de referido.");
      return;
    }

    try {
      setError("");
      setReferralLoading(true);
      const response = await organizerGiftReferralLinkApi(eventId);
      const info = response?.data?.info || null;
      setReferralInfo(info);
      if (info?.url) {
        await copyTextToClipboard(info.url);
        setCopyFeedbackTick((current) => current + 1);
        try { toast.success("Link de referido copiado."); } catch (_) {}
      }
    } catch (err) {
      console.error(err);
      handleError(err);
    } finally {
      setReferralLoading(false);
    }
  };

  const handleCopyReferralLink = async () => {
    if (!referralInfo?.url) return;
    try {
      await copyTextToClipboard(referralInfo.url);
      setCopyFeedbackTick((current) => current + 1);
      try { toast.success("Link de referido copiado."); } catch (_) {}
    } catch (err) {
      console.error(err);
      handleError(err);
    }
  };

  const handleShareReferralLink = async () => {
    if (!referralInfo?.url) return;
    try {
      if (navigator?.share) {
        await navigator.share({
          title: referralInfo?.eventName || "Link de referido",
          text: "Comprá desde este link",
          url: referralInfo.url,
        });
        return;
      }
      await handleCopyReferralLink();
    } catch (err) {
      if (err?.name === "AbortError") return;
      console.error(err);
      handleError(err);
    }
  };

  if (loading) return <SpinLoading />;

  return (
    <div className="organizer-page organizer-referrals-page">
      <section className="organizer-section">
        {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

        <div className="organizer-form-grid">
          <div className="organizer-card organizer-card--seller-referral">
            <div className="organizer-card__header organizer-card__header--stacked">
              <div>
                <h3 className="organizer-card__title">Referidos</h3>
                <p className="organizer-card__subtitle">
                  Genera tu link para compartir el evento y atribuir ventas a tu perfil.
                </p>
              </div>
            </div>

            <div className="seller-referral-card">
              <div className="organizer-field-group">
                <label className="organizer-field-label">Evento</label>
                <OrganizerPillSelect
                  placeholder="Selecciona evento"
                  value={eventId}
                  onChange={(val) => setEventId(val ? String(val) : "")}
                  options={eventOptions}
                />
              </div>

              {eventId ? (
                <>
                  <div className="seller-referral-card__meta">
                    <span className="seller-referral-card__label">Evento seleccionado</span>
                    <strong>{referralInfo?.eventName || selectedEventLabel}</strong>
                  </div>

                  <div className="seller-referral-card__actions">
                    <button
                      type="button"
                      className="organizer-btn"
                      onClick={handleGenerateReferralLink}
                      disabled={referralLoading}
                    >
                      {referralLoading ? "Generando..." : referralInfo?.url ? "Regenerar link" : "Generar link"}
                    </button>
                    <button
                      type="button"
                      className={`organizer-btn organizer-btn-ghost seller-referral-card__copy-btn${
                        copyFeedbackTick ? " is-copied" : ""
                      }`}
                      onClick={handleCopyReferralLink}
                      disabled={!referralInfo?.url}
                      aria-live="polite"
                    >
                      {copyFeedbackTick ? "Link copiado" : "Copiar link"}
                    </button>
                    <button
                      type="button"
                      className="organizer-btn organizer-btn-ghost"
                      onClick={handleShareReferralLink}
                      disabled={!referralInfo?.url}
                    >
                      Compartir
                    </button>
                  </div>

                  <div
                    className={`seller-referral-card__copy-feedback${
                      copyFeedbackTick ? " is-visible" : ""
                    }`}
                    aria-live="polite"
                  >
                    Link copiado al portapapeles.
                  </div>

                  <div className="seller-referral-card__field">
                    <span className="seller-referral-card__label">URL</span>
                    <div className={`seller-referral-card__url${copyFeedbackTick ? " is-copied" : ""}`}>
                      {referralInfo?.url || "Todavía no generaste un link para este evento."}
                    </div>
                  </div>

                  {referralInfo?.code && (
                    <div className="seller-referral-card__foot">
                      <span>Código interno: {referralInfo.code}</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="organizer-empty-card seller-referral-card__empty">
                  Selecciona un evento para generar tu link de referido.
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OrganizerReferrals;
